import React from 'react';
import { useApp } from '../../context/AppContext';
import {
  WEATHER_OPTIONS, LIGHT_OPTIONS, CROWD_LEVEL_OPTIONS,
  DIFFICULTY_OPTIONS, TIME_WINDOW_OPTIONS
} from '../../constants/spotEnums';

/**
 * ClickableMetadataBadge - Affiche un badge de métadonnée V2 cliquable
 * Quand on clique, ça filtre les spots par cette métadonnée
 */
export function ClickableMetadataBadge({ 
  category,  // 'time_windows' | 'weather' | 'light' | 'crowd_level' | 'difficulty'
  value,     // La valeur du filtre (ex: 'sunrise', 'clear', etc.)
  showLabel = true,
  size = 'sm', // 'xs' | 'sm' | 'md'
  className = ''
}) {
  const { state, actions } = useApp();
  
  // Récupérer les options pour cette catégorie
  const getOptions = () => {
    switch (category) {
      case 'time_windows': return TIME_WINDOW_OPTIONS;
      case 'weather': return WEATHER_OPTIONS;
      case 'light': return LIGHT_OPTIONS;
      case 'crowd_level': return CROWD_LEVEL_OPTIONS;
      case 'difficulty': return DIFFICULTY_OPTIONS;
      default: return [];
    }
  };
  
  const options = getOptions();
  const option = options.find(o => o.value === value);
  
  if (!option) return null;
  
  // Vérifier si ce filtre est actif
  const isActive = state.selectedMetadataFilters[category]?.includes(value);
  
  // Couleurs par catégorie
  const getCategoryStyles = () => {
    const baseStyles = {
      time_windows: {
        bg: 'bg-amber-50',
        activeBg: 'bg-amber-200',
        text: 'text-amber-700',
        border: 'border-amber-300',
        hover: 'hover:bg-amber-100'
      },
      weather: {
        bg: 'bg-sky-50',
        activeBg: 'bg-sky-200',
        text: 'text-sky-700',
        border: 'border-sky-300',
        hover: 'hover:bg-sky-100'
      },
      light: {
        bg: 'bg-orange-50',
        activeBg: 'bg-orange-200',
        text: 'text-orange-700',
        border: 'border-orange-300',
        hover: 'hover:bg-orange-100'
      },
      crowd_level: {
        bg: value === 'very_quiet' || value === 'quiet' ? 'bg-green-50' :
            value === 'popular' || value === 'crowded' ? 'bg-red-50' : 'bg-yellow-50',
        activeBg: value === 'very_quiet' || value === 'quiet' ? 'bg-green-200' :
                  value === 'popular' || value === 'crowded' ? 'bg-red-200' : 'bg-yellow-200',
        text: value === 'very_quiet' || value === 'quiet' ? 'text-green-700' :
              value === 'popular' || value === 'crowded' ? 'text-red-700' : 'text-yellow-700',
        border: value === 'very_quiet' || value === 'quiet' ? 'border-green-300' :
                value === 'popular' || value === 'crowded' ? 'border-red-300' : 'border-yellow-300',
        hover: value === 'very_quiet' || value === 'quiet' ? 'hover:bg-green-100' :
               value === 'popular' || value === 'crowded' ? 'hover:bg-red-100' : 'hover:bg-yellow-100'
      },
      difficulty: {
        bg: value === 'easy' ? 'bg-green-50' : value === 'hard' ? 'bg-red-50' : 'bg-yellow-50',
        activeBg: value === 'easy' ? 'bg-green-200' : value === 'hard' ? 'bg-red-200' : 'bg-yellow-200',
        text: value === 'easy' ? 'text-green-700' : value === 'hard' ? 'text-red-700' : 'text-yellow-700',
        border: value === 'easy' ? 'border-green-300' : value === 'hard' ? 'border-red-300' : 'border-yellow-300',
        hover: value === 'easy' ? 'hover:bg-green-100' : value === 'hard' ? 'hover:bg-red-100' : 'hover:bg-yellow-100'
      }
    };
    return baseStyles[category] || baseStyles.time_windows;
  };
  
  const styles = getCategoryStyles();
  
  // Tailles
  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };
  
  const handleClick = (e) => {
    e.stopPropagation();
    actions.toggleMetadataFilter(category, value);
  };
  
  return (
    <button
      onClick={handleClick}
      className={`
        inline-flex items-center gap-1 rounded-full cursor-pointer
        transition-all duration-200 border
        ${sizeClasses[size]}
        ${isActive ? styles.activeBg : styles.bg}
        ${styles.text}
        ${isActive ? styles.border : 'border-transparent'}
        ${styles.hover}
        ${isActive ? 'ring-2 ring-offset-1 ring-current ring-opacity-30' : ''}
        ${className}
      `}
      title={`Filtrer par: ${option.label}`}
    >
      <span>{option.icon}</span>
      {showLabel && <span>{option.label}</span>}
      {isActive && <span className="ml-0.5">✓</span>}
    </button>
  );
}

/**
 * MetadataFilterSummary - Affiche un résumé des filtres actifs (métadonnées + tags)
 */
export function MetadataFilterSummary() {
  const { state, actions } = useApp();
  const { selectedMetadataFilters, selectedTags, allTags, tagCategories } = state;
  
  // Compter le total des filtres actifs
  const totalMetadataFilters = Object.values(selectedMetadataFilters).flat().length;
  const totalTagFilters = selectedTags?.length || 0;
  const totalFilters = totalMetadataFilters + totalTagFilters;
  
  if (totalFilters === 0) return null;

  // Helper pour trouver les infos d'un tag
  const getTagInfo = (tagSlug) => {
    const tag = allTags?.find(t => t.slug === tagSlug);
    const category = tag ? tagCategories?.find(c => c.id === tag.category_id) : null;
    return { tag, category };
  };

  // Handler pour supprimer un tag
  const handleRemoveTag = (tagSlug) => {
    actions.setSelectedTags(selectedTags.filter(t => t !== tagSlug));
  };

  // Handler pour tout effacer
  const handleClearAll = () => {
    actions.clearMetadataFilters();
    actions.setSelectedTags([]);
  };
  
  return (
    <div className="flex items-center gap-2 flex-wrap bg-blue-50 rounded-lg p-2">
      <span className="text-xs font-medium text-blue-700">Filtres actifs:</span>
      
      {/* Filtres de métadonnées */}
      {Object.entries(selectedMetadataFilters).map(([category, values]) =>
        values.map(value => (
          <ClickableMetadataBadge 
            key={`${category}-${value}`}
            category={category}
            value={value}
            size="xs"
          />
        ))
      )}
      
      {/* Tags sélectionnés */}
      {selectedTags?.map(tagSlug => {
        const { tag, category } = getTagInfo(tagSlug);
        return (
          <button
            key={tagSlug}
            onClick={() => handleRemoveTag(tagSlug)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-200 ring-2 ring-offset-1"
            style={{
              backgroundColor: category?.color || '#3b82f6',
              color: 'white',
              ringColor: category?.color || '#3b82f6'
            }}
            title={`Retirer le filtre: ${tag?.name || tagSlug}`}
          >
            #{tag?.name || tagSlug}
            <span>✓</span>
          </button>
        );
      })}
      
      <button
        onClick={handleClearAll}
        className="text-xs text-red-500 hover:text-red-700 underline ml-2"
      >
        Effacer tout
      </button>
    </div>
  );
}

export default ClickableMetadataBadge;
